<script setup lang="ts">
  import { computed, ref } from 'vue'
  import {
    Plus,
    ChevronDown,
    Info,
    Link,
    Ellipsis,
    Eye,
    Copy,
    ArrowUp,
    ArrowDown,
    FolderTree,
    Trash2,
    Scale,
    User,
    Repeat,
    ArrowUpIcon,
    ArrowDownIcon
  } from 'lucide-vue-next'
  import { Switch } from '@/components/ui/switch'
  import type {
    SuperUserQuestionnaire as Questionnaire,
    QuestionnaireQuestion
  } from '@/types/generated'
  import { TagRelationshipEnum } from '@/types/generated/types.gen'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import { useTranslation } from '@/composables/useTranslation'
  import UserQuestionnaireQuestion from '@/components/app/questionnaire/editor/UserQuestionnaireQuestion.vue'
  import { ChevronsUpDown, ChevronsDownUp } from 'lucide-vue-next'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { useI18n } from 'vue-i18n'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator
  } from '@/components/ui/dropdown-menu'
  import QuestionPreviewDialog from './QuestionPreviewDialog.vue'
  import UnlinkFieldIndicator from '@/components/icons/UnlinkFieldIndicator.vue'
  import VirtualQuestionContent from './VirtualQuestionContent.vue'

  const { t } = useTranslation({
    en: {
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      questionCount: '{count} question | {count} questions',
      addQuestion: 'Add question',
      required: 'Required',
      lawSpecificSection: 'Law-specific section',
      participantSection: 'Participant section',
      connectedParticipant: 'Connected participant',
      repeatable: 'Repeatable',
      helpText: 'Help text (Optional)',
      autofill: 'Autofill (Optional)',
      autofillDescription:
        'If corresponding data is available in the case, this question will be filled automatically',
      inputType: 'Input type',
      notSelected: 'Not selected',
      preview: 'Preview',
      duplicate: 'Duplicate',
      moveUp: 'Move up',
      moveDown: 'Move down',
      moveToSection: 'Move to section',
      delete: 'Delete',
      addOption: 'Add option',
      logicJump: 'Logic jump',
      switchToMultipleChoice: 'Switch to multiple choice to add more options',
      goToQuestion: 'Go to question...',
      shortText: 'Short text',
      booleanYesNo: 'Boolean (Yes/No)',
      singleChoice: 'Single choice',
      multipleChoice: 'Multiple choice',
      fileUpload: 'File upload',
      date: 'Date',
      yes: 'Yes',
      no: 'No',
      facingLegalAction: 'Facing legal action',
      consideringLegalAction: 'Considering initiating legal action',
      seekingLegalAdvice: 'Seeking legal advice only',
      other: 'Other',
      goToUploadDocuments: 'Go to: Upload relevant documents',
      goToWhenNeedAssistance: 'Go to: When do you need legal assistance?',
      email: 'Email',
      phone: 'Phone',
      whatsapp: 'WhatsApp',
      fax: 'Fax',
      mail: 'Mail',
      connectedParticipantTooltip:
        'Select the type of participant this section will collect information about',
      repeatableTooltip: 'Allow multiple instances of this participant section to be added',
      virtualQuestionTitle: 'Would you like to add another {participantType}?',
      virtualQuestionBadge: 'Auto-generated',
      virtualQuestionDescription:
        'This question is automatically shown to users when this section is repeatable. It cannot be edited.',
      repeatSection: 'Repeat section',
      goToNextSection: 'Go to next section',
      autoGeneratedTooltip:
        'This question was created automatically. If you want to remove it, turn off the "Repeatable" option.'
    },
    de: {
      expandAll: 'Alle ausklappen',
      collapseAll: 'Alle einklappen',
      questionCount: '{count} Frage | {count} Fragen',
      addQuestion: 'Frage hinzufügen',
      required: 'Erforderlich',
      lawSpecificSection: 'Rechtsspezifischer Abschnitt',
      participantSection: 'Teilnehmerabschnitt',
      connectedParticipant: 'Verbundener Teilnehmer',
      repeatable: 'Wiederholbar',
      connectedParticipantTooltip:
        'Wählen Sie den Teilnehmertyp aus, über den dieser Abschnitt Informationen sammeln soll',
      repeatableTooltip: 'Erlaube mehrere Instanzen dieses Teilnehmerabschnitts hinzuzufügen',
      helpText: 'Hilfstext (Optional)',
      autofill: 'Autofill (Optional)',
      autofillDescription:
        'Falls entsprechende Daten im Fall verfügbar sind, wird diese Frage automatisch ausgefüllt',
      inputType: 'Eingabetyp',
      notSelected: 'Nicht ausgewählt',
      preview: 'Vorschau',
      duplicate: 'Duplizieren',
      moveUp: 'Nach oben',
      moveDown: 'Nach unten',
      moveToSection: 'In Abschnitt verschieben',
      delete: 'Löschen',
      addOption: 'Option hinzufügen',
      logicJump: 'Logik-Sprung',
      switchToMultipleChoice: 'Zu Mehrfachauswahl wechseln, um weitere Optionen hinzuzufügen',
      goToQuestion: 'Zu Frage gehen...',
      shortText: 'Kurzer Text',
      booleanYesNo: 'Boolean (Ja/Nein)',
      singleChoice: 'Einzelauswahl',
      multipleChoice: 'Mehrfachauswahl',
      fileUpload: 'Datei-Upload',
      date: 'Datum',
      yes: 'Ja',
      no: 'Nein',
      facingLegalAction: 'Mit rechtlichen Schritten konfrontiert',
      consideringLegalAction: 'Erwägt rechtliche Schritte einzuleiten',
      seekingLegalAdvice: 'Nur rechtliche Beratung suchen',
      other: 'Andere',
      goToUploadDocuments: 'Gehe zu: Relevante Dokumente hochladen',
      goToWhenNeedAssistance: 'Gehe zu: Wann benötigen Sie rechtliche Unterstützung?',
      email: 'E-Mail',
      phone: 'Telefon',
      whatsapp: 'WhatsApp',
      fax: 'Fax',
      mail: 'Post',
      virtualQuestionTitle: 'Möchten Sie einen weiteren {participantType} hinzufügen?',
      virtualQuestionBadge: 'Automatisch generiert',
      virtualQuestionDescription:
        'Diese Frage wird den Benutzern automatisch angezeigt, wenn dieser Abschnitt wiederholbar ist. Sie kann nicht bearbeitet werden.',
      repeatSection: 'Abschnitt wiederholen',
      goToNextSection: 'Zum nächsten Abschnitt',
      autoGeneratedTooltip:
        'Diese Frage wurde automatisch erstellt. Wenn Sie sie entfernen möchten, deaktivieren Sie die Option "Wiederholbar".'
    }
  })

  const props = defineProps<{
    questionnaire: Questionnaire
    sectionUuid: string
  }>()

  const questionnaireEditorStore = useQuestionnaireEditorStore()
  const isExpanded = ref(true)
  const isVirtualQuestionExpanded = ref(true)
  const questionExpandedStates = ref<Record<string, boolean>>({})
  const { locale } = useI18n()
  const section = computed(() =>
    props.questionnaire.schema?.find((s) => s.uuid === props.sectionUuid)
  )

  // Question preview dialog state
  const previewDialogOpen = ref(false)
  const previewQuestion = ref<QuestionnaireQuestion | null>(null)

  const openQuestionPreview = (question: QuestionnaireQuestion) => {
    previewQuestion.value = question
    previewDialogOpen.value = true
  }

  // Available sections for move to section menu
  const availableSections = computed(() => {
    return (
      props.questionnaire.schema?.map((s) => ({
        uuid: s.uuid,
        title: s.title || 'Untitled Section'
      })) ?? []
    )
  })

  // Question actions
  const duplicateQuestion = (question: QuestionnaireQuestion) => {
    if (section.value) {
      questionnaireEditorStore.copyQuestion(section.value, question)
    }
  }

  const deleteQuestion = (question: QuestionnaireQuestion) => {
    if (section.value) {
      questionnaireEditorStore.removeQuestion(section.value, question)
    }
  }

  const questionCount = computed(() => {
    const baseCount = section.value?.items?.length || 0
    const hasVirtualQuestion = (section.value as any)?.repeatable && selectedParticipantName.value
    return baseCount + (hasVirtualQuestion ? 1 : 0)
  })

  // Get all participant types with their translations (formatted for SelectField)
  const participantTypes = computed<Option[]>(() => {
    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN

    // Get all values from TagRelationshipEnum
    return Object.values(TagRelationshipEnum)
      .map((value) => ({
        value: value,
        title: entityTranslations[value as keyof typeof entityTranslations] || value
      }))
      .sort((a, b) => a.title.localeCompare(b.title))
  })

  // Section type options
  const sectionTypeOptions = computed<Option[]>(() => [
    { value: 'law_specific', title: t('lawSpecificSection'), icon: Scale },
    { value: 'participant', title: t('participantSection'), icon: User }
  ])

  // Get the display name for the selected participant type
  const selectedParticipantName = computed<string | null>(() => {
    if (!section.value || (section.value as any).type !== 'participant') return null
    const participantType = (section.value as any).participant_type
    if (!participantType) return null

    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN
    return entityTranslations[participantType as keyof typeof entityTranslations] || participantType
  })

  // Get the virtual question title
  const virtualQuestionTitle = computed<string>(() => {
    if (!selectedParticipantName.value) return 'Would you like to add another participant?'
    return t('virtualQuestionTitle', { participantType: selectedParticipantName.value })
  })

  // Handle section type change
  const handleSectionTypeChange = (value: string) => {
    if (!section.value) return
    ;(section.value as any).type = value

    // Clear connected fields from all questions in this section
    if (section.value.items) {
      section.value.items.forEach((question) => {
        question.tag = null
        question.tag_relationship = null
      })
    }

    if (value === 'participant') {
      // Auto-select first participant type if not already set
      if (!(section.value as any).participant_type && participantTypes.value.length > 0) {
        ;(section.value as any).participant_type = participantTypes.value[0].value
      }
    } else {
      // Clear participant fields when switching away from participant type
      ;(section.value as any).participant_type = null
      ;(section.value as any).repeatable = false
    }
  }

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value

    // Toggle all questions to match the section state
    if (section.value?.items) {
      section.value.items.forEach((question) => {
        questionExpandedStates.value[question.uuid] = isExpanded.value
      })
    }

    // Also toggle virtual question if it exists
    isVirtualQuestionExpanded.value = isExpanded.value
  }

  const toggleQuestionExpanded = (questionUuid: string) => {
    questionExpandedStates.value[questionUuid] = !questionExpandedStates.value[questionUuid]
  }

  const isQuestionExpanded = (questionUuid: string) => {
    return questionExpandedStates.value[questionUuid] ?? true
  }

  const addQuestion = () => {
    if (section.value) {
      questionnaireEditorStore.addQuestion(section.value, section.value.items?.length || 0)
    }
  }

  const addQuestionAtIndex = (index: number) => {
    if (section.value) {
      questionnaireEditorStore.addQuestion(section.value, index)
    }
  }

  const moveQuestionUp = (question: QuestionnaireQuestion) => {
    if (section.value) {
      questionnaireEditorStore.moveQuestionUp(section.value, question)
    }
  }

  const moveQuestionDown = (question: QuestionnaireQuestion) => {
    if (section.value) {
      questionnaireEditorStore.moveQuestionDown(section.value, question)
    }
  }

  const moveQuestionToSection = (targetSectionUuid: string, question: QuestionnaireQuestion) => {
    if (!section.value) return
    const targetSection = props.questionnaire.schema?.find((s) => s.uuid === targetSectionUuid)
    if (targetSection) {
      questionnaireEditorStore.moveQuestionToSection(section.value, targetSection, question)
    }
  }

  // Helper functions
  const isFirstQuestion = (question: QuestionnaireQuestion) => {
    return section.value?.items?.indexOf(question) === 0
  }

  const isLastQuestion = (question: QuestionnaireQuestion) => {
    const index = section.value?.items?.indexOf(question)
    return index === (section.value?.items?.length ?? 0) - 1
  }
</script>

<template>
  <div
    v-if="section"
    class="inline-flex w-full flex-col items-center justify-start gap-2 p-4"
  >
    <div
      class="flex w-full flex-col rounded-xl bg-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1
        outline-offset-[-1px] outline-gray-200"
    >
      <!-- Section Header Row -->
      <div class="inline-flex items-center justify-start gap-2 p-4">
        <div class="inline-flex flex-1 flex-col items-start justify-start">
          <input
            v-model="section.title"
            type="text"
            placeholder="Untitled Section"
            class="w-full cursor-text border-0 bg-transparent p-0 font-['Inter'] text-lg font-semibold leading-7
              text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
          <div class="justify-start font-['Inter'] text-xs font-normal leading-none text-gray-600">
            {{ t('questionCount', { count: questionCount }) }}
          </div>
        </div>

        <!-- Section Type Selector -->
        <div class="section-type-selector w-[220px]">
          <SelectField
            v-model="(section as any).type"
            name="section-type"
            :options="sectionTypeOptions"
            @update:model-value="handleSectionTypeChange"
          />
        </div>

        <button
          class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 p-1.5
            hover:bg-gray-50"
          :title="isExpanded ? t('collapseAll') : t('expandAll')"
          @click="toggleExpanded"
        >
          <ChevronsDownUp
            v-if="isExpanded"
            class="h-4 w-4"
          />
          <ChevronsUpDown
            v-if="!isExpanded"
            class="h-4 w-4"
          />
        </button>
      </div>

      <!-- Participant Section Fields -->
      <div
        v-if="(section as any).type === 'participant'"
        class="flex flex-col"
      >
        <!-- Horizontal Divider -->
        <div class="border-t border-gray-200"></div>

        <!-- Connected Participant and Repeatable Row -->
        <div class="flex items-end gap-4 p-4">
          <!-- Connected Participant (vertical stack) -->
          <div class="flex flex-1 flex-col gap-2">
            <!-- Label with Info Icon -->
            <div class="flex items-center gap-1.5">
              <label class="text-sm font-medium text-gray-700">
                {{ t('connectedParticipant') }}
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <button
                      type="button"
                      class="inline-flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <Info class="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    :show-arrow="true"
                    class="max-w-xs"
                  >
                    <p>{{ t('connectedParticipantTooltip') }}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <!-- SelectField -->
            <SelectField
              v-model="(section as any).participant_type"
              name="participant-type"
              :options="participantTypes"
              :is-filterable="true"
            />
          </div>

          <!-- Repeatable (horizontal inline) -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-700">
              {{ t('repeatable') }}
            </label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger as-child>
                  <button
                    type="button"
                    class="inline-flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <Info class="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  variant="secondary"
                  :show-arrow="true"
                  class="max-w-xs"
                >
                  <p>{{ t('repeatableTooltip') }}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch
              :checked="(section as any).repeatable || false"
              @update:checked="(checked) => ((section as any).repeatable = checked)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="w-full space-y-2">
      <div
        v-for="(question, index) in section.items"
        :key="question.uuid"
        class="w-full space-y-2"
      >
        <div class="flex w-full justify-center">
          <button
            class="inline-flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
              hover:bg-gray-50"
            @click="addQuestionAtIndex(index)"
          >
            <div class="relative h-5 w-5 overflow-hidden">
              <Plus class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
            </div>
            <div class="flex items-center justify-center gap-1.5 px-1.5">
              <div
                class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
              >
                {{ t('addQuestion') }}
              </div>
            </div>
          </button>
        </div>

        <div
          class="flex flex-col items-start justify-start self-stretch overflow-hidden rounded-xl bg-white
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
        >
          <div
            class="inline-flex h-16 items-center justify-start gap-2 self-stretch border-b border-gray-200 bg-white p-4"
          >
            <!-- Link/Unlink Indicator -->
            <div class="flex items-center justify-center">
              <Link
                v-if="question.tag && question.tag !== ''"
                class="h-5 w-5 text-emerald-600"
              />
              <UnlinkFieldIndicator v-else />
            </div>

            <div class="flex flex-1 items-center justify-start gap-2">
              <input
                v-model="question.label"
                type="text"
                placeholder="Untitled Question"
                class="flex-1 cursor-text border-0 bg-transparent p-0 font-['Inter'] text-lg font-semibold leading-7
                  text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
            </div>

            <div class="flex items-center justify-start gap-4">
              <div class="flex items-center justify-start gap-2">
                <div
                  class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
                >
                  {{ t('required') }}
                </div>
                <Switch
                  :checked="!question.optional"
                  @update:checked="(checked) => (question.optional = !checked)"
                />
              </div>
              <!-- Question Actions Menu -->
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <button
                    class="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white p-1.5 shadow-sm
                      transition-colors hover:bg-gray-50"
                    style="box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.04)"
                  >
                    <Ellipsis class="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  class="w-48"
                >
                  <DropdownMenuItem @click="openQuestionPreview(question)">
                    <Eye class="mr-2 h-4 w-4" />
                    {{ t('preview') }}
                  </DropdownMenuItem>
                  <DropdownMenuItem @click="duplicateQuestion(question)">
                    <Copy class="mr-2 h-4 w-4" />
                    {{ t('duplicate') }}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    :disabled="isFirstQuestion(question)"
                    @click="moveQuestionUp(question)"
                  >
                    <ArrowUp class="mr-2 h-4 w-4" />
                    {{ t('moveUp') }}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    :disabled="isLastQuestion(question)"
                    @click="moveQuestionDown(question)"
                  >
                    <ArrowDown class="mr-2 h-4 w-4" />
                    {{ t('moveDown') }}
                  </DropdownMenuItem>

                  <!-- Move to Section Submenu -->
                  <DropdownMenu v-if="availableSections.length > 1">
                    <DropdownMenuTrigger
                      class="flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none
                        focus:bg-accent data-[state=open]:bg-accent"
                    >
                      <FolderTree class="mr-2 h-4 w-4" />
                      {{ t('moveToSection') }}
                      <ChevronDown class="ml-auto h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      side="right"
                      align="start"
                      class="w-48"
                    >
                      <DropdownMenuItem
                        v-for="targetSection in availableSections.filter(
                          (s) => s.uuid !== section?.uuid
                        )"
                        :key="targetSection.uuid"
                        @click="moveQuestionToSection(targetSection.uuid, question)"
                      >
                        {{ targetSection.title }}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    class="text-red-600 focus:text-red-600"
                    @click="deleteQuestion(question)"
                  >
                    <Trash2 class="mr-2 h-4 w-4" />
                    {{ t('delete') }}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <div class="flex items-center justify-start gap-2">
                <button
                  class="flex h-8 w-8 items-center justify-center rounded-md bg-white p-1.5
                    shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
                    hover:bg-gray-50"
                  @click="toggleQuestionExpanded(question.uuid)"
                >
                  <ChevronDown
                    :class="[
                      'h-4 w-4 transition-transform duration-200',
                      isQuestionExpanded(question.uuid) ? 'rotate-180' : 'rotate-0'
                    ]"
                  />
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="isQuestionExpanded(question.uuid)"
            class="flex flex-col items-start justify-start gap-4 self-stretch p-4"
          >
            <UserQuestionnaireQuestion
              :uuid="question.uuid"
              :section-uuid="section.uuid"
              :questionnaire="questionnaire"
              :show-header="false"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Virtual Question Preview for Repeatable Sections -->
    <div
      v-if="(section as any).repeatable && selectedParticipantName"
      class="w-full space-y-2"
    >
      <!-- Add Question Button before virtual question -->
      <div class="flex w-full justify-center">
        <button
          class="inline-flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
            hover:bg-gray-50"
          @click="addQuestion"
        >
          <div class="relative h-5 w-5 overflow-hidden">
            <Plus class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
          </div>
          <div class="flex items-center justify-center gap-1.5 px-1.5">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('addQuestion') }}
            </div>
          </div>
        </button>
      </div>

      <!-- Virtual Question Card (matches regular question styling) -->
      <div
        class="flex flex-col items-start justify-start self-stretch overflow-hidden rounded-xl bg-white
          shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
      >
        <!-- Question Header (matches regular question header) -->
        <div
          class="inline-flex h-16 items-center justify-start gap-2 self-stretch border-b border-gray-200 bg-white p-4"
        >
          <!-- Repeat indicator with tooltip -->
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <div class="flex items-center justify-center">
                  <Repeat class="h-5 w-5 text-gray-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent
                variant="secondary"
                :show-arrow="true"
                class="max-w-xs"
              >
                <p>{{ t('autoGeneratedTooltip') }}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div class="flex flex-1 items-center justify-start gap-2">
            <span class="flex-1 font-['Inter'] text-lg font-semibold leading-7 text-gray-800">
              {{ t('virtualQuestionTitle', { participantType: selectedParticipantName }) }}
            </span>
          </div>

          <div class="flex items-center justify-start gap-4">
            <!-- Required toggle (always on, disabled for virtual question) -->
            <div class="flex items-center justify-start gap-2">
              <div
                class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
              >
                {{ t('required') }}
              </div>
              <Switch
                :checked="true"
                disabled
              />
            </div>

            <!-- Expand/Collapse button -->
            <div class="flex items-center justify-start gap-2">
              <button
                class="flex h-8 w-8 items-center justify-center rounded-md bg-white p-1.5
                  shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
                  hover:bg-gray-50"
                @click="isVirtualQuestionExpanded = !isVirtualQuestionExpanded"
              >
                <ChevronDown
                  :class="[
                    'h-4 w-4 transition-transform duration-200',
                    isVirtualQuestionExpanded ? 'rotate-180' : 'rotate-0'
                  ]"
                />
              </button>
            </div>
          </div>
        </div>

        <!-- Question Content (matches UserQuestionnaireQuestion structure) -->
        <div
          v-if="isVirtualQuestionExpanded"
          class="flex flex-col self-stretch p-4"
        >
          <VirtualQuestionContent :question-title="virtualQuestionTitle" />
        </div>
      </div>
    </div>
    <button
      v-if="!((section as any).repeatable && selectedParticipantName)"
      class="inline-flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
        shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
        hover:bg-gray-50"
      @click="addQuestion"
    >
      <div class="relative h-5 w-5 overflow-hidden">
        <Plus class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
      </div>
      <div class="flex items-center justify-center gap-1.5 px-1.5">
        <div class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800">
          {{ t('addQuestion') }}
        </div>
      </div>
    </button>
    <!-- Question Preview Dialog -->
    <QuestionPreviewDialog
      v-model:open="previewDialogOpen"
      :question="previewQuestion"
      :section-uuid="sectionUuid"
    />
  </div>
</template>

<style scoped>
  /* Make section type selector button match collapse button height (h-8 = 32px) */
  .section-type-selector :deep(button[role='combobox']) {
    height: 2rem !important; /* 32px = h-8 */
    min-height: 2rem !important;
    max-height: 2rem !important;
    padding-top: 0.375rem !important; /* p-1.5 = 6px top */
    padding-bottom: 0.375rem !important; /* p-1.5 = 6px bottom */
    line-height: 1.25rem !important; /* Ensure consistent line height */
  }
</style>
